/**
 * Suggestion Service
 * Handles operations related to suggested Linkers/study partners
 */

import { 
  collection, 
  query, 
  where, 
  limit, 
  getDocs, 
  orderBy, 
  doc, 
  getDoc,
  serverTimestamp,
  setDoc,
  increment,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';
import { getAuth } from 'firebase/auth';


/**
 * Fetch random suggestions when no matches are found
 * 
 * @param {number} limitCount - Number of suggestions to fetch
 * @returns {Promise<Array>} Array of random user profiles
 */
const fetchRandomSuggestions = async (limitCount = 5) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to fetch suggestions');
    }
    
    // Get existing connections to exclude from suggestions
    const connectionsQuery = query(
      collection(db, 'connections'),
      where('users', 'array-contains', currentUser.uid)
    );
    
    const connectionsSnapshot = await getDocs(connectionsQuery);
    const connectedUserIds = connectionsSnapshot.docs.map(doc => {
      const data = doc.data();
      return data.users.find(uid => uid !== currentUser.uid);
    });
    
    // Also exclude users with pending connection requests
    const sentRequestsQuery = query(
      collection(db, 'connectionRequests'),
      where('senderId', '==', currentUser.uid)
    );
    
    const receivedRequestsQuery = query(
      collection(db, 'connectionRequests'),
      where('recipientId', '==', currentUser.uid)
    );
    
    const [sentSnapshot, receivedSnapshot] = await Promise.all([
      getDocs(sentRequestsQuery),
      getDocs(receivedRequestsQuery)
    ]);
    
    const pendingUserIds = [
      ...sentSnapshot.docs.map(doc => doc.data().recipientId),
      ...receivedSnapshot.docs.map(doc => doc.data().senderId)
    ];
    
    // Combine all user IDs to exclude
    const excludeUserIds = [currentUser.uid, ...connectedUserIds, ...pendingUserIds];
    
    // Get random profiles
    const randomProfilesQuery = query(
      collection(db, 'profiles'),
      limit(limitCount * 3) // Fetch more to account for filtering
    );
    
    const randomProfilesSnapshot = await getDocs(randomProfilesQuery);
    const randomSuggestions = [];
    
    for (const profileDoc of randomProfilesSnapshot.docs) {
      if (excludeUserIds.includes(profileDoc.id) || 
          randomSuggestions.some(s => s.id === profileDoc.id)) {
        continue; // Skip excluded or already added users
      }
      
      const profileData = profileDoc.data();
      
      // Assign a random match score
      const randomMatchScore = 0.1 + (Math.random() * 0.3); // Between 0.1 and 0.4
      
      randomSuggestions.push({
        id: profileDoc.id,
        ...profileData,
        matchScore: randomMatchScore,
        matchPercentage: Math.min(Math.round(randomMatchScore * 100), 60), // Cap at 60%
        isRandom: true // Mark as random suggestion
      });
      
      if (randomSuggestions.length >= limitCount) {
        break;
      }
    }
    
    return randomSuggestions;
  } catch (error) {
    console.error("Error fetching random suggestions:", error);
    throw error;
  }
};


/**
 * Fetch suggested Linkers/study partners for the current user
 * 
 * @param {number} limitCount - Number of suggestions to fetch (default: 5)
 * @param {Object} filters - Optional filters for suggestions
 * @returns {Promise<Array>} Array of suggested user profiles
 */
const fetchSuggestions = async (limitCount = 5, filters = {}) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to fetch suggestions');
    }
    
    // Get current user profile for matching
    const userProfileRef = doc(db, 'profiles', currentUser.uid);
    const userProfileSnap = await getDoc(userProfileRef);
    
    if (!userProfileSnap.exists()) {
      throw new Error('User profile not found');
    }
    
    const userProfile = userProfileSnap.data();
    
    // Get existing connections to exclude from suggestions
    const connectionsQuery = query(
      collection(db, 'connections'),
      where('users', 'array-contains', currentUser.uid)
    );
    
    const connectionsSnapshot = await getDocs(connectionsQuery);
    const connectedUserIds = connectionsSnapshot.docs.map(doc => {
      const data = doc.data();
      return data.users.find(uid => uid !== currentUser.uid);
    });
    
    // Also exclude users with pending connection requests
    const sentRequestsQuery = query(
      collection(db, 'connectionRequests'),
      where('senderId', '==', currentUser.uid)
    );
    
    const receivedRequestsQuery = query(
      collection(db, 'connectionRequests'),
      where('recipientId', '==', currentUser.uid)
    );
    
    const [sentSnapshot, receivedSnapshot] = await Promise.all([
      getDocs(sentRequestsQuery),
      getDocs(receivedRequestsQuery)
    ]);
    
    const pendingUserIds = [
      ...sentSnapshot.docs.map(doc => doc.data().recipientId),
      ...receivedSnapshot.docs.map(doc => doc.data().senderId)
    ];
    
    // Combine all user IDs to exclude
    const excludeUserIds = [currentUser.uid, ...connectedUserIds, ...pendingUserIds];
    
    // Build the base query for profiles
    let profilesQuery = query(
      collection(db, 'profiles'),
      limit(limitCount * 3) // Fetch more than we need to allow for filtering
    );
    
    // Apply university filter if user has university info
    if (userProfile.academic?.university && !filters.ignoreUniversity) {
      profilesQuery = query(
        collection(db, 'profiles'),
        where('academic.university', '==', userProfile.academic.university),
        limit(limitCount * 3)
      );
    }
    
    const profilesSnapshot = await getDocs(profilesQuery);
    let suggestions = [];
    
    // Process and filter profiles
    for (const profileDoc of profilesSnapshot.docs) {
      if (excludeUserIds.includes(profileDoc.id)) {
        continue; // Skip excluded users
      }
      
      const profileData = profileDoc.data();
      
      // Calculate match score based on common attributes
      const matchScore = calculateMatchScore(userProfile, profileData);
      
      if (matchScore > 0) {
        suggestions.push({
          id: profileDoc.id,
          ...profileData,
          matchScore,
          matchPercentage: Math.min(Math.round(matchScore * 100), 99) // Cap at 99%
        });
      }
    }
    
    // If we don't have enough suggestions, get random profiles
    if (suggestions.length < limitCount) {
      // Try again without university filter
      if (userProfile.academic?.university && !filters.ignoreUniversity) {
        const randomSuggestions = await fetchSuggestions(limitCount, { ...filters, ignoreUniversity: true });
        
        // Add random suggestions that aren't already in our list
        for (const suggestion of randomSuggestions) {
          if (!suggestions.some(s => s.id === suggestion.id) && suggestions.length < limitCount) {
            suggestions.push(suggestion);
          }
        }
      } else {
        // If still not enough, get completely random profiles
        const randomProfilesQuery = query(
          collection(db, 'profiles'),
          limit(limitCount * 2) // Fetch more to account for filtering
        );
        
        const randomProfilesSnapshot = await getDocs(randomProfilesQuery);
        
        for (const profileDoc of randomProfilesSnapshot.docs) {
          if (excludeUserIds.includes(profileDoc.id) || 
              suggestions.some(s => s.id === profileDoc.id)) {
            continue; // Skip excluded or already added users
          }
          
          const profileData = profileDoc.data();
          
          // Assign a lower random match score
          const randomMatchScore = 0.1 + (Math.random() * 0.3); // Between 0.1 and 0.4
          
          suggestions.push({
            id: profileDoc.id,
            ...profileData,
            matchScore: randomMatchScore,
            matchPercentage: Math.min(Math.round(randomMatchScore * 100), 60), // Cap at 60%
            isRandom: true // Mark as random suggestion
          });
          
          if (suggestions.length >= limitCount) {
            break;
          }
        }
      }
    }
    
    // Sort by match score (descending)
    suggestions.sort((a, b) => b.matchScore - a.matchScore);
    
    // Limit to requested count
    return suggestions.slice(0, limitCount);
  } catch (error) {
    console.error("Error fetching suggestions:", error);
    throw error;
  }
};

/**
 * Calculate match score between two profiles (0 to 1)
 * 
 * @param {Object} userProfile - Current user profile
 * @param {Object} otherProfile - Other user profile
 * @returns {number} Match score (0 to 1)
 */
const calculateMatchScore = (userProfile, otherProfile) => {
  let score = 0;
  let factors = 0;
  
  // University match (strongest factor)
  if (userProfile.academic?.university && otherProfile.academic?.university) {
    factors += 3;
    if (userProfile.academic.university === otherProfile.academic.university) {
      score += 3;
    }
  }
  
  // Degree match
  if (userProfile.academic?.degree && otherProfile.academic?.degree) {
    factors += 2;
    if (userProfile.academic.degree === otherProfile.academic.degree) {
      score += 2;
    }
  }
  
  // Year of study match
  if (userProfile.academic?.yearOfStudy && otherProfile.academic?.yearOfStudy) {
    factors += 1;
    if (userProfile.academic.yearOfStudy === otherProfile.academic.yearOfStudy) {
      score += 1;
    }
  }
  
  // Interests match
  if (userProfile.bio?.interests && otherProfile.bio?.interests) {
    const userInterests = userProfile.bio.interests;
    const otherInterests = otherProfile.bio.interests;
    
    if (userInterests.length > 0 && otherInterests.length > 0) {
      factors += 2;
      
      // Count common interests
      const commonInterests = userInterests.filter(interest => 
        otherInterests.includes(interest)
      );
      
      // Score based on proportion of common interests
      const interestScore = commonInterests.length / Math.max(userInterests.length, 5);
      score += interestScore * 2;
    }
  }
  
  // Personality match
  if (userProfile.bio?.personalities && otherProfile.bio?.personalities) {
    const userPersonalities = userProfile.bio.personalities;
    const otherPersonalities = otherProfile.bio.personalities;
    
    if (userPersonalities.length > 0 && otherPersonalities.length > 0) {
      factors += 1;
      
      // Count common personalities
      const commonPersonalities = userPersonalities.filter(personality => 
        otherPersonalities.includes(personality)
      );
      
      // Score based on proportion of common personalities
      const personalityScore = commonPersonalities.length / Math.max(userPersonalities.length, 3);
      score += personalityScore;
    }
  }
  
  // Avoid division by zero
  if (factors === 0) {
    return 0.1; // Minimal match if no comparison factors available
  }
  
  // Normalize score to 0-1 range
  return score / (factors * 1.2); // Slightly reduce to avoid too many perfect matches
};


const suggestionService = {
  fetchSuggestions,
  fetchRandomSuggestions
};

export default suggestionService;